import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { HttpInterceptor, HttpRequest, HttpResponse, HttpHandler, HttpEvent } from '@angular/common/http';
import { reportBackendError } from './error-handler/backend-error';
import { EMPTY, Observable, of, throwError } from 'rxjs';
import { mergeMap, catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { DEV_ENV_NAME } from './constants/env';
import { AuthService } from './services/auth.service';

export interface IHttpRequestError extends Error {
    status?: number;
    url?: string;
}

@Injectable()
export class HttpConfigInterceptor implements HttpInterceptor {
    constructor(
        private router: Router,
        private injector: Injector
    ) { }

    private get authService(): AuthService {
        return this.injector.get(AuthService);
    }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return next.handle(request).pipe(
            catchError(err => {
                // The API itself answers 200 with 401 in the body; a real 401 status comes from the
                // backend's session re-check (session revoked, company or user switched elsewhere).
                // The redirect is the whole answer: the caller's error branch would only put
                // "server unavailable" over the login page and report it to Sentry (#339).
                if (err.status === 401 && this.redirectToLogin(request)) {
                    return EMPTY;
                }
                const errBody = err.error || {};
                // Text fields only: an object there - the SyntaxError Angular puts in place of a 200
                // body that did not parse, an object-valued "error" - came out as "{}", hiding the
                // request and the status that err.message names (#372).
                const detail = [errBody.message, errBody.detail, errBody.error, errBody.Error, err.message, err.statusText, err]
                    .find(field => typeof field === 'string' && field !== '');
                // Callers read the text through getErrorMessage; a bare string or object reached
                // Sentry as "<unknown>" or "Non-Error exception captured" (#339).
                const error: IHttpRequestError = new Error(detail || JSON.stringify(err));
                error.status = err.status;
                error.url = err.url || request.urlWithParams;
                return throwError(error);
            })
        )
        .pipe(mergeMap((event: HttpEvent<any>) => {
            if (event instanceof HttpResponse) {
                if (event.body.responseCode == 401) {
                    // The API's own 401 (no token, or a rejected one) gets the real one's answer: the page's
                    // branch put the error window up, sent the user to /auth/logout over this redirect and
                    // rejected with a bare 401 (#369). The login pages still get the response - a token
                    // rejected right after login is theirs to report.
                    if (this.redirectToLogin(request) && !this.onAuthPage) {
                        return EMPTY;
                    }
                } else if (event.body.responseCode == 500) {
                    if (event.body.error) {
                        if (environment.envName === DEV_ENV_NAME) {
                            console.error("TSTERR intercept", event.body.error);
                        } else {
                            console.error(new Error(event.body.error));
                            reportBackendError(event.body.error, {
                                handlerSource: "interceptor",
                                requestUrl: request.urlWithParams,
                            });
                        }
                    }
                } else if (event.status !== 200){
                    console.error("HTTP interceptor error", event.body.error || event);
                }
            }
            return of(event);
        }));
    }

    private get currentRoute(): string {
        return this.router.url || window.location.hash.replace(/^#/, '');
    }

    private get onAuthPage(): boolean {
        return this.currentRoute.indexOf('/auth/') === 0;
    }

    /** Returns false when the request opted out and nothing was done. */
    private redirectToLogin(request: HttpRequest<any>): boolean {
        // Requests tagged X-Background-Request (e.g. CallService's automatic, native
        // call-triggered lead lookup) fire invisibly to the user, often alongside their
        // own navigation on completion. Forcing navigation to /auth/login here would race
        // with that, leaving the app on a blank screen (issue #313). Any future
        // fire-and-forget/background request can opt out the same way.
        if (request.headers.has('X-Background-Request')) {
            return false;
        }
        if (!this.onAuthPage) {
            this.authService.rememberOidcRetryRoute(this.currentRoute);
        }
        this.router.navigateByUrl("/auth/login");
        return true;
    }
}
